import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})
const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000
const AUTH_INIT_WARNING_MS = 5000
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
const SUPPORTED_LANGUAGES = ['en', 'es']

const getLastActivityKey = (userId) => `user_${userId}_last_activity`
const getLanguagePreferenceKey = (userId) => `user_${userId}_language`
const getSavedLanguagePreference = (userId) => {
  const language = localStorage.getItem(getLanguagePreferenceKey(userId))
  return SUPPORTED_LANGUAGES.includes(language) ? language : 'en'
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [loading, setLoading] = useState(true)
  const { i18n } = useTranslation()
  const inactivityTimeoutRef = useRef(null)
  const currentUserRef = useRef(null)

  const clearInactivityTimeout = () => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current)
      inactivityTimeoutRef.current = null
    }
  }

  const getLastActivity = (userId) => {
    const storedValue = localStorage.getItem(getLastActivityKey(userId))
    const parsedValue = Number(storedValue)
    return Number.isFinite(parsedValue) ? parsedValue : null
  }

  const recordActivity = (userId) => {
    localStorage.setItem(getLastActivityKey(userId), Date.now().toString())
  }

  const clearActivity = (userId) => {
    if (userId) {
      localStorage.removeItem(getLastActivityKey(userId))
    }
  }

  const scheduleInactivityLogout = (userId) => {
    clearInactivityTimeout()

    const lastActivity = getLastActivity(userId) ?? Date.now()
    const elapsed = Date.now() - lastActivity
    const timeoutMs = Math.max(INACTIVITY_TIMEOUT_MS - elapsed, 0)

    inactivityTimeoutRef.current = setTimeout(async () => {
      const activeUser = currentUserRef.current
      if (!activeUser || activeUser.id !== userId) return

      localStorage.setItem(getLanguagePreferenceKey(userId), i18n.language)
      clearActivity(userId)
      await i18n.changeLanguage('en')
      await supabase.auth.signOut().catch((error) => {
        console.error('Error signing out after inactivity:', error)
      })
      setUser(null)
      setUserRole(null)
    }, timeoutMs)
  }

  const isSessionExpired = (userId) => {
    const lastActivity = getLastActivity(userId)
    if (!lastActivity) return false
    return Date.now() - lastActivity >= INACTIVITY_TIMEOUT_MS
  }

  useEffect(() => {
    currentUserRef.current = user
  }, [user])

  useEffect(() => {
    let isActive = true
    let initWarningTimeout = null

    // Get initial session
    const getSession = async () => {
      initWarningTimeout = setTimeout(() => {
        console.warn('Auth session restore is taking longer than expected')
      }, AUTH_INIT_WARNING_MS)

      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        clearTimeout(initWarningTimeout)

        if (!isActive) return

        if (error) {
          console.error('Error getting session:', error)
          setLoading(false)
          return
        }

        if (session?.user && isSessionExpired(session.user.id)) {
          clearActivity(session.user.id)
          await supabase.auth.signOut().catch((signOutError) => {
            console.error('Error signing out expired session:', signOutError)
          })
          if (!isActive) return
          await i18n.changeLanguage('en')
          setUser(null)
          setUserRole(null)
          setLoading(false)
          return
        }

        if (session?.user) {
          await i18n.changeLanguage(getSavedLanguagePreference(session.user.id))
          if (!isActive) return
        }

        setUser(session?.user ?? null)

        if (session?.user) {
          recordActivity(session.user.id)
          scheduleInactivityLogout(session.user.id)
          fetchUserRole(session.user.email)
        }

        setLoading(false)
      } catch (error) {
        clearTimeout(initWarningTimeout)
        if (!isActive) return
        console.error('Error in getSession:', error)
        setLoading(false)
      }
    }

    getSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        try {
          const previousUserId = currentUserRef.current?.id

          if (session?.user && isSessionExpired(session.user.id)) {
            clearActivity(session.user.id)
            await supabase.auth.signOut().catch((signOutError) => {
              console.error('Error signing out expired session:', signOutError)
            })
            await i18n.changeLanguage('en')
            if (!isActive) return
            setUser(null)
            setUserRole(null)
            setLoading(false)
            return
          }

          if (session?.user) {
            await i18n.changeLanguage(getSavedLanguagePreference(session.user.id))
            if (!isActive) return
          } else {
            await i18n.changeLanguage('en')
            if (!isActive) return
          }

          setUser(session?.user ?? null)

          if (session?.user) {
            recordActivity(session.user.id)
            scheduleInactivityLogout(session.user.id)
            fetchUserRole(session.user.email)
          } else {
            clearInactivityTimeout()
            clearActivity(previousUserId)
            setUserRole(null)
          }

          setLoading(false)
        } catch (error) {
          console.error('Error in auth state change:', error)
          setLoading(false)
        }
      }
    )

    return () => {
      isActive = false
      if (initWarningTimeout) {
        clearTimeout(initWarningTimeout)
      }
      subscription.unsubscribe()
      clearInactivityTimeout()
    }
  }, [])

  useEffect(() => {
    if (!user?.id) {
      clearInactivityTimeout()
      return
    }

    const handleActivity = () => {
      recordActivity(user.id)
      scheduleInactivityLogout(user.id)
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleActivity()
      }
    }

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true })
    })
    document.addEventListener('visibilitychange', handleVisibilityChange)

    scheduleInactivityLogout(user.id)

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity)
      })
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [user?.id])

  const fetchUserRole = async (email) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('role')
        .eq('email', email)
        .single()

      if (error) {
        console.error('Error fetching user role:', error)
        setUserRole(null)
        return
      }
      
      setUserRole(data?.role || null)
    } catch (error) {
      console.error('Error in fetchUserRole:', error)
      setUserRole(null)
    }
  }

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  }

  const signOut = async () => {
    // Save current language preference before signing out
    if (user) {
      localStorage.setItem(getLanguagePreferenceKey(user.id), i18n.language)
    }

    await i18n.changeLanguage('en')

    // Always clear local auth state so the UI reliably returns to the
    // login screen, even if the network is down or Supabase is
    // unreachable. The remote sign-out is best-effort.
    const { error } = await supabase.auth.signOut()
    clearInactivityTimeout()
    clearActivity(user?.id)
    setUser(null)
    setUserRole(null)
    return { error }
  }

  const saveLanguagePreference = (language) => {
    if (user && SUPPORTED_LANGUAGES.includes(language)) {
      localStorage.setItem(getLanguagePreferenceKey(user.id), language)
    }
  }

  const value = {
    user,
    userRole,
    loading,
    signIn,
    signOut,
    saveLanguagePreference,
    isAdmin: userRole === 'admin',
    isCustomer: userRole === 'customer'
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
